import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, apiUrl } from "../lib/api";
import { useSapWarningToast } from "../lib/use-sap-warning-toast";
import { useSapSyncToast, type SapSyncResult } from "../lib/use-sap-sync-toast";

const KEY = "offers";

export interface PendingOfferAttachment {
	index: number;
	name: string;
}

export function usePendingOfferAttachments(offerId: string | null) {
	return useQuery({
		queryKey: [KEY, "pending-attachments", offerId],
		queryFn: () =>
			api.get<{ items: PendingOfferAttachment[] }>(`/api/offers/${offerId}/pending-attachments`),
		enabled: !!offerId,
	});
}

export function pendingOfferAttachmentDownloadUrl(
	offerId: string,
	index: number,
	options?: { inline?: boolean },
): string {
	const params = new URLSearchParams({ index: String(index) });
	if (options?.inline) params.set("disposition", "inline");
	return apiUrl(`/api/offers/${offerId}/pending-attachments/download?${params.toString()}`);
}

export function useUploadPendingOfferAttachments() {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: ({ offerId, files }: { offerId: string; files: File[] }) => {
			const formData = new FormData();
			for (const file of files) formData.append("files", file);
			return api.upload<{ staged: number }>(`/api/offers/${offerId}/pending-attachments`, formData);
		},
		onSuccess: (_result, vars) => {
			void qc.invalidateQueries({ queryKey: [KEY, "pending-attachments", vars.offerId] });
		},
	});
}

export function useDeletePendingOfferAttachment() {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: ({ offerId, index }: { offerId: string; index: number }) =>
			api.delete<{ ok: boolean }>(`/api/offers/${offerId}/pending-attachments?index=${index}`),
		onSuccess: (_result, vars) => {
			void qc.invalidateQueries({ queryKey: [KEY, "pending-attachments", vars.offerId] });
		},
	});
}

export function useOfferList(options?: {
	companyId?: string | undefined;
	opportunityId?: string | undefined;
	assignedTo?: string | undefined;
	status?: string | undefined;
	search?: string | undefined;
	offerDateFrom?: string | undefined;
	offerDateTo?: string | undefined;
	createdAtFrom?: string | undefined;
	createdAtTo?: string | undefined;
	emailSent?: boolean | undefined;
	sort?: string | undefined;
	dir?: "asc" | "desc" | undefined;
	page?: number;
	limit?: number;
	enabled?: boolean;
}) {
	const page = options?.page ?? 1;
	const limit = options?.limit ?? 20;
	const offset = (page - 1) * limit;
	return useQuery({
		queryKey: [KEY, "list", options],
		queryFn: () => {
			const params = new URLSearchParams();
			params.set("limit", String(limit));
			params.set("offset", String(offset));
			if (options?.companyId) params.set("companyId", options.companyId);
			if (options?.opportunityId) params.set("opportunityId", options.opportunityId);
			if (options?.assignedTo) params.set("assignedTo", options.assignedTo);
			if (options?.status) params.set("status", options.status);
			if (options?.search) params.set("search", options.search);
			if (options?.offerDateFrom) params.set("offerDateFrom", options.offerDateFrom);
			if (options?.offerDateTo) params.set("offerDateTo", options.offerDateTo);
			if (options?.createdAtFrom) params.set("createdAtFrom", options.createdAtFrom);
			if (options?.createdAtTo) params.set("createdAtTo", options.createdAtTo);
			if (options?.emailSent !== undefined) params.set("emailSent", String(options.emailSent));
			if (options?.sort) params.set("sort", options.sort);
			if (options?.dir) params.set("dir", options.dir);
			return api.get<{ items: Offer[]; total: number }>(`/api/offers?${params.toString()}`);
		},
		enabled: options?.enabled !== false,
	});
}

export function useOfferDetail(id: string) {
	return useQuery({
		queryKey: [KEY, "detail", id],
		queryFn: () => api.get<Offer & { lines: OfferLine[] }>(`/api/offers/${id}`),
		enabled: !!id,
	});
}

export function useCreateOffer() {
	const qc = useQueryClient();
	const warnOnSapGap = useSapWarningToast();
	const reportSapSync = useSapSyncToast();
	return useMutation({
		mutationFn: (data: CreateOfferInput) => api.post<Offer & SapSyncResult>("/api/offers", data),
		onSuccess: (offer) => {
			warnOnSapGap(offer);
			if (offer.sapSync?.reason === "lead_without_company") reportSapSync(offer);
			qc.invalidateQueries({ queryKey: [KEY, "list"] });
		},
	});
}

export function useUpdateOffer() {
	const qc = useQueryClient();
	const warnOnSapGap = useSapWarningToast();
	const reportSapSync = useSapSyncToast();
	return useMutation({
		mutationFn: ({ id, data }: { id: string; data: Partial<CreateOfferInput> }) =>
			api.patch<Offer & SapSyncResult>(`/api/offers/${id}`, data),
		onSuccess: (offer, vars) => {
			warnOnSapGap(offer);
			reportSapSync(offer);
			qc.invalidateQueries({ queryKey: [KEY, "list"] });
			qc.invalidateQueries({ queryKey: [KEY, "detail", vars.id] });
		},
	});
}

export function useDuplicateOffer() {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: (id: string) => api.post<Offer>(`/api/offers/${id}/duplicate`, {}),
		onSuccess: (_, id) => {
			qc.invalidateQueries({ queryKey: [KEY, "list"] });
			qc.invalidateQueries({ queryKey: [KEY, "detail", id] });
		},
	});
}

export function useRetryOfferSync() {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: (id: string) =>
			api.post<{ ok: boolean; message: string }>(`/api/offers/${id}/retry-sync`, {}),
		onSuccess: () => qc.invalidateQueries({ queryKey: [KEY, "list"] }),
	});
}

export function useCancelOffer() {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: (id: string) => api.post<Offer>(`/api/offers/${id}/cancel`, {}),
		onSuccess: (_, id) => {
			qc.invalidateQueries({ queryKey: [KEY, "list"] });
			qc.invalidateQueries({ queryKey: [KEY, "detail", id] });
		},
	});
}

export function useDeleteOffer() {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: (id: string) => api.delete<void>(`/api/offers/${id}`),
		onSuccess: () => qc.invalidateQueries({ queryKey: [KEY, "list"] }),
	});
}

export function useSendOffer() {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: (id: string) => api.post<Offer>(`/api/offers/${id}/send`, {}),
		onSuccess: (_, id) => {
			qc.invalidateQueries({ queryKey: [KEY, "list"] });
			qc.invalidateQueries({ queryKey: [KEY, "detail", id] });
		},
	});
}

export function useAcceptOffer() {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: (id: string) => api.post<Offer>(`/api/offers/${id}/accept`, {}),
		onSuccess: (_, id) => {
			qc.invalidateQueries({ queryKey: [KEY, "list"] });
			qc.invalidateQueries({ queryKey: [KEY, "detail", id] });
		},
	});
}

export interface FoundDatasheet {
	description: string;
	path: string;
}

export interface OfferEmailPreview {
	email: string;
	cc: string;
	subject: string;
	message: string;
	defaultSubject: string;
	defaultMessage: string;
	hasCustomTemplate: boolean;
	recipientName: string;
	foundDatasheets: FoundDatasheet[];
	missingDatasheets: string[];
	excludedDatasheets: string[];
	noNextcloud: boolean;
	signatureMissingReason: string | null;
	contractId: string | null;
	contractDefaultLang: "en" | "tr";
}

export function useOfferEmailPreview() {
	return useMutation({
		mutationFn: (id: string) => api.get<OfferEmailPreview>(`/api/offers/${id}/email-preview`),
	});
}

export type OfferDatasheets = Pick<
	OfferEmailPreview,
	"foundDatasheets" | "missingDatasheets" | "excludedDatasheets" | "noNextcloud"
>;

// email-preview EN klasöründen arar; TR seçilince liste TR klasöründen yeniden çekilir.
export function useOfferDatasheets(id: string, lang: "en" | "tr", enDatasheets: OfferDatasheets) {
	return useQuery({
		queryKey: [KEY, "datasheets", id, lang],
		queryFn: () => api.get<OfferDatasheets>(`/api/offers/${id}/datasheets?lang=${lang}`),
		initialData: lang === "en" ? enDatasheets : undefined,
		staleTime: 5 * 60 * 1000,
	});
}

export function useSaveOfferMailTemplate() {
	return useMutation({
		mutationFn: (input: { subject: string | null; body: string | null }) =>
			api.patch("/auth/offer-mail-template", input),
	});
}

export interface SendOfferEmailInput {
	id: string;
	email: string;
	cc?: string;
	subject: string;
	message: string;
	lang: "en" | "tr";
	variant?: "offer" | "proforma";
	bankName?: string;
	currency?: string;
	bankName2?: string;
	currency2?: string;
	includeContract?: boolean;
	contractId?: string;
	contractLang?: "en" | "tr";
}

export function useSendOfferEmail() {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: ({ id, ...data }: SendOfferEmailInput) =>
			api.post<{
				sent: boolean;
				missingDatasheets: string[];
				offer: Offer;
				nextcloudSaveWarning: string | null;
			}>(`/api/offers/${id}/send-email`, data),
		onSuccess: (_, vars) => {
			qc.invalidateQueries({ queryKey: [KEY, "list"] });
			qc.invalidateQueries({ queryKey: [KEY, "detail", vars.id] });
		},
	});
}

export function useConvertToOrder() {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: ({
			id,
			alternativeGroups,
			deliveryDate,
			paymentTerms,
			shippingAddress,
			shippingAddressId,
			lineWarehouses,
		}: {
			id: string;
			alternativeGroups?: number[] | undefined;
			deliveryDate: string;
			paymentTerms: string;
			shippingAddress?: string | undefined;
			shippingAddressId?: string | undefined;
			lineWarehouses?: Record<string, string | null> | undefined;
		}) =>
			api.post<{ id: string }>(`/api/offers/${id}/convert-to-order`, {
				...(alternativeGroups?.length ? { alternativeGroups } : {}),
				deliveryDate,
				paymentTerms,
				...(shippingAddressId ? { shippingAddressId, shippingAddress } : {}),
				...(lineWarehouses && Object.keys(lineWarehouses).length ? { lineWarehouses } : {}),
			}),
		onSuccess: () => {
			qc.invalidateQueries({ queryKey: [KEY, "list"] });
			qc.invalidateQueries({ queryKey: ["orders", "list"] });
		},
	});
}

export interface Offer {
	/** Kaydedildi, ama muhatabın SAP kartı olmadığı için SAP'a gönderilemeyecek. */
	sapWarning?: string | null;
	syncStatus?: "pending" | "in_progress" | "synced" | "failed" | "manual_review" | null;
	/** Teklif potansiyel müşteriye açıldıysa dolu; muhataba dönüşünce boşalır. */
	leadId?: string | null;
	isLeadOffer?: boolean;
	createdByName?: string | null;
	createdByEmail?: string | null;
	syncError?: string | null;
	companySyncError?: string | null;
	sapDocNum?: number | null;
	sapDocEntry?: number | null;
	id: string;
	documentNumber: string | null;
	offerDate: string | null;
	validUntil: string | null;
	currency: string | null;
	exchangeRate: string | null;
	paymentTerms: string | null;
	deliveryTerms: string | null;
	notes: string | null;
	internalNotes: string | null;
	pdfNotes: string | null;
	buyerExtraInfo: string | null;
	contractNo: string | null;
	contractDate: string | null;
	commitmentDate: string | null;
	commitmentValue: string | null;
	commitmentUnit: string | null;
	offerOrderDeliveryType: string | null;
	termsCommissioning: string | null;
	termsAssembly: string | null;
	termsVat: string | null;
	termsQuality: string | null;
	termsValidity: string | null;
	termsWarranty: string | null;
	termsPayment: string | null;
	termsDelivery: string | null;
	termsPaymentMethod: string | null;
	termsI18n: Record<string, Record<string, string>> | null;
	startupDescription: string | null;
	installationDescription: string | null;
	tmsDescription: string | null;
	atsDescription: string | null;
	factorySpecialNotes: string | null;
	generalNotes: string | null;
	isTaxIncluded: boolean;
	showGtipInPdf: boolean;
	subtotal: string | null;
	vatTotal: string | null;
	grossTotal: string | null;
	total: string | null;
	status:
		| "draft"
		| "sent"
		| "accepted"
		| "rejected"
		| "expired"
		| "cancelled"
		| "converted_to_order";
	sentAt: string | null;
	emailSentAt: string | null;
	versionNumber: number;
	isLatestVersion: boolean;
	parentOfferId: string | null;
	companyId: string | null;
	companySapAccountId: string | null;
	/** Seçilen hesabın kendi para birimi (offer.currency'den farklı olabilir; fiyat listesi/kalem seçimi teklifin currency'sini belirler). */
	companySapAccountCurrency: string | null;
	companyName: string | null;
	contactPersonId: string | null;
	opportunityId: string | null;
	assignedTo: string | null;
	assignedToName?: string | null;
	assignedToEmail?: string | null;
	createdAt: string;
	updatedAt: string;
}

interface OfferLine {
	id: string;
	lineNumber: number;
	itemCode: string | null;
	itemDescription: string | null;
	displayDescription: string | null;
	quantity: string;
	lineDetail: string | null;
	alternativeGroup: number | null;
	alternativeCurrency: string | null;
	unit: string | null;
	unitPrice: string;
	givenUnitPrice: string | null;
	vatRate: string | null;
	vatGroup: string | null;
	discountPercent: string | null;
	lineTotal: string | null;
	isFreeText: boolean;
	showInPdf: boolean;
	showPriceInPdf: boolean;
	showDiscountInPdf: boolean;
	sendDatasheetInEmail: boolean;
	isCustomDesign: boolean;
	notes: string | null;
	factorySpecialNotes: string | null;
	generalNotes: string | null;
	priceListModel: string | null;
	commitmentValue: string | null;
	commitmentUnit: string | null;
	stockItemId: string | null;
	isSection: boolean;
	sectionItems: { name: string; quantity: number; unitPrice: number }[] | null;
	/** Birim fiyata katlanmış kalemler (alternatör, kabin, ATS/TMŞ panoları). */
	priceComponents: { label: string; price: number; separate?: boolean }[] | null;
}

interface CreateOfferInput {
	offerDate?: string | undefined;
	validUntil?: string | undefined;
	currency?: string | undefined;
	exchangeRate?: number | undefined;
	paymentTerms?: string | undefined;
	deliveryTerms?: string | undefined;
	notes?: string | undefined;
	internalNotes?: string | undefined;
	pdfNotes?: string | undefined;
	buyerExtraInfo?: string | undefined;
	contractNo?: string | undefined;
	contractDate?: string | undefined;
	commitmentDate?: string | undefined;
	commitmentValue?: string | undefined;
	commitmentUnit?: string | undefined;
	offerOrderDeliveryType?: string | undefined;
	termsCommissioning?: string | undefined;
	termsAssembly?: string | undefined;
	termsVat?: string | undefined;
	termsQuality?: string | undefined;
	termsValidity?: string | undefined;
	termsWarranty?: string | undefined;
	termsPayment?: string | undefined;
	termsDelivery?: string | undefined;
	termsPaymentMethod?: string | undefined;
	termsI18n?: Record<string, Record<string, string>> | undefined;
	startupDescription?: string | undefined;
	installationDescription?: string | undefined;
	tmsDescription?: string | undefined;
	atsDescription?: string | undefined;
	factorySpecialNotes?: string | undefined;
	generalNotes?: string | undefined;
	isTaxIncluded?: boolean | undefined;
	showGtipInPdf?: boolean | undefined;
	companyId?: string | undefined;
	companySapAccountId?: string | null | undefined;
	contactPersonId?: string | undefined;
	opportunityId?: string | undefined;
	assignedTo?: string | undefined;
	lines: {
		lineNumber: number;
		itemCode?: string | undefined;
		itemDescription?: string | undefined;
		displayDescription?: string | undefined;
		quantity: number;
		lineDetail?: string | undefined;
		unit?: string | undefined;
		unitPrice: number;
		givenUnitPrice?: number | undefined;
		vatRate?: number | undefined;
		vatGroup?: string | undefined;
		discountPercent?: number | undefined;
		lineTotal?: number | undefined;
		notes?: string | undefined;
		priceListModel?: string | undefined;
		commitmentValue?: string | undefined;
		commitmentUnit?: "day" | "week" | "month" | undefined;
		stockItemId?: string | undefined;
		isFreeText?: boolean | undefined;
		showInPdf?: boolean | undefined;
		showPriceInPdf?: boolean | undefined;
		showDiscountInPdf?: boolean | undefined;
		sendDatasheetInEmail?: boolean | undefined;
		isCustomDesign?: boolean | undefined;
		isSection?: boolean | undefined;
		sectionItems?: { name: string; quantity: number; unitPrice: number }[] | undefined;
	}[];
}
